import React, { useState, useEffect, useRef } from "react";
import { getCsrfToken } from "../../../../public/api/authApi";
import {
  GlobalButton,
  GlobalTextField,
  GlobalSelectField,
} from "../../../../global/forms/GlobalFormFields";
import GlobalFormShell from "../../../../global/forms/GlobalFormShell";
import { GlobalModal } from "../../../../global/GlobalModal";
import GlobalDataTable from "../../../../global/GlobalDataTable";
import GlobalBadge from "../../../../global/GlobalBadge";
import GlobalSuccessFlashModal from "../../../../global/GlobalSuccessFlashModal";
import { GlobalTableActionButton } from "../../../../global/GlobalTableActionButton";

const STATUS_OPTIONS = [
  { value: 1, label: "Active" },
  { value: 2, label: "Inactive" },
];

const PropertyMasterPanel = ({
  title,
  nameField,
  nameLabel,
  fetchList,
  createItem,
  updateItem,
  deleteItem,
  onDirectoryChange,
}) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    status_id: 1,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [formError, setFormError] = useState(null);
  const [formErrorDetails, setFormErrorDetails] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const alertRef = useRef(null);
  const formSectionRef = useRef(null);
  const [successMsg, setSuccessMsg] = useState("");

  const loadRows = async () => {
    try {
      setLoading(true);
      const response = await fetchList();
      const data = response?.data ?? response ?? [];
      setRows(Array.isArray(data) ? data : []);
    } catch (err) {
      setError(err.message || `Failed to load ${title}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadRows();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setFormField = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (formErrorDetails[field]) {
      setFormErrorDetails((prev) => ({ ...prev, [field]: "" }));
    }
    setFormError(null);
  };

  const scrollToForm = () => {
    requestAnimationFrame(() => {
      formSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "nearest" });
    });
  };

  const handleSubmit = async (e) => {
    if (e?.preventDefault) e.preventDefault();
    setFormError(null);
    setFormErrorDetails({});
    setSuccessMsg("");

    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }

      const payload = {
        [nameField]: formData.name.trim(),
        status_id: Number(formData.status_id),
      };

      let result;
      if (isEditing) {
        result = await updateItem(formData.id, payload, csrfToken);
      } else {
        result = await createItem(payload, csrfToken);
      }

      if (result?.error) {
        setFormError(result.error.message || `Failed to save ${title}`);
        setFormErrorDetails(result.error.details || {});
        setTimeout(() => alertRef.current?.focus(), 0);
        return;
      }

      setSuccessMsg(
        result?.reactivated
          ? `${title} restored to the directory.`
          : isEditing
            ? `${title} updated successfully.`
            : `${title} created successfully.`,
      );
      handleCancel();
      await loadRows();
      onDirectoryChange?.();
    } catch (err) {
      setFormError(err.message);
      setTimeout(() => alertRef.current?.focus(), 0);
    }
  };

  const handleEdit = (row) => {
    setSuccessMsg("");
    setFormData({
      id: row.id,
      name: row[nameField] || "",
      status_id: row.status_id != null ? Number(row.status_id) : 1,
    });
    setIsEditing(true);
    scrollToForm();
  };

  const handleConfirmDelete = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      let csrfToken = localStorage.getItem("csrf-token");
      if (!csrfToken) {
        csrfToken = await getCsrfToken();
        localStorage.setItem("csrf-token", csrfToken);
      }
      await deleteItem(itemToDelete.id, csrfToken);
      setShowDeleteConfirm(false);
      setItemToDelete(null);
      setSuccessMsg(`${title} removed from the directory.`);
      await loadRows();
      onDirectoryChange?.();
    } catch (err) {
      alert(`Failed to remove: ${err.message}`);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancel = () => {
    setFormData({ id: null, name: "", status_id: 1 });
    setFormError(null);
    setFormErrorDetails({});
    setIsEditing(false);
  };

  if (loading) return <p className="ub-loading">Loading {title}…</p>;
  if (error) return <p className="ub-error">Error: {error}</p>;

  const ALERT_ID = `property-master-${nameField}-alert`;

  return (
    <div className="ub-master-section">
      <GlobalSuccessFlashModal message={successMsg} onDismiss={() => setSuccessMsg("")} />

      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "1.25rem",
        }}
      >
        <h3 className="ub-section__subhead" style={{ margin: 0 }}>
          {title}
        </h3>
      </div>

      <div ref={formSectionRef} style={{ scrollMarginTop: "1rem" }}>
        <GlobalFormShell
          legend={isEditing ? `Edit ${title.toLowerCase()}` : `Add ${title.toLowerCase()}`}
          showAlert={!!formError}
          alertText={formError}
          alertId={ALERT_ID}
          formAlertRef={alertRef}
          actions={
            <>
              {isEditing ? (
                <GlobalButton variant="ghost" onClick={handleCancel}>
                  Cancel edit
                </GlobalButton>
              ) : null}
              <GlobalButton variant="primary" onClick={handleSubmit}>
                {isEditing ? "Save" : "Create"}
              </GlobalButton>
            </>
          }
        >
          <GlobalTextField
            id={`${nameField}_input`}
            label={nameLabel}
            value={formData.name}
            onChange={(v) => setFormField("name", v)}
            error={formErrorDetails[nameField]}
            baseAlertId={ALERT_ID}
            required
          />
          {isEditing ? (
            <GlobalSelectField
              id={`${nameField}_status`}
              label="Status"
              value={String(formData.status_id)}
              onChange={(v) => setFormField("status_id", Number(v))}
              options={STATUS_OPTIONS}
              error={formErrorDetails.status_id}
              baseAlertId={ALERT_ID}
            />
          ) : null}
        </GlobalFormShell>
      </div>

      <GlobalDataTable
        wrapClassName="ub-table-wrap"
        tableClassName="ub-table"
        tableAriaLabel={`${title} master table`}
        headers={[
          { key: "name", label: nameLabel },
          { key: "status", label: "Status" },
          { key: "created", label: "Created" },
          { key: "actions", label: "Actions", className: "ub-table__col-actions" },
        ]}
      >
        {rows.length === 0 ? (
          <tr>
            <td colSpan={4} style={{ textAlign: "center", padding: "1.5rem", color: "#6b7280" }}>
              No records yet.
            </td>
          </tr>
        ) : (
          rows.map((row) => (
            <tr key={row.id}>
              <td>{row[nameField]}</td>
              <td>
                <GlobalBadge active={Number(row.status_id) === 1} />
              </td>
              <td>
                {row.created_at
                  ? new Date(row.created_at).toLocaleDateString("en-GB")
                  : "—"}
              </td>
              <td className="ub-table__col-actions">
                <div className="ub-table-actions">
                  <GlobalTableActionButton action="edit" onClick={() => handleEdit(row)} />
                  <GlobalTableActionButton
                    action="delete"
                    onClick={() => {
                      setItemToDelete(row);
                      setShowDeleteConfirm(true);
                    }}
                  />
                </div>
              </td>
            </tr>
          ))
        )}
      </GlobalDataTable>

      <GlobalModal
        open={showDeleteConfirm}
        onClose={() => !isDeleting && setShowDeleteConfirm(false)}
        title={`Delete ${title}?`}
        footer={
          <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
            <GlobalButton onClick={() => setShowDeleteConfirm(false)} disabled={isDeleting}>
              Cancel
            </GlobalButton>
            <GlobalButton variant="danger" onClick={handleConfirmDelete} loading={isDeleting}>
              Confirm delete
            </GlobalButton>
          </div>
        }
      >
        <p>
          Remove <b>{itemToDelete?.[nameField]}</b> from the directory?
        </p>
      </GlobalModal>
    </div>
  );
};

export default PropertyMasterPanel;
